import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, money, publicQuoteUrl, adminPdfUrl, fileUrl, cadastroClienteUrl, formatDateTime, buildSistemaWhatsappMessage, openWhatsapp } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Upload, Copy, FileDown, Eye, MessageCircle, Save, X, GripVertical, ExternalLink, CheckCircle2, XCircle, RotateCcw, ChevronDown, ChevronUp, Check, ImageIcon, Bell, AlertTriangle, Send, Scissors, ArrowUp, ArrowDown,
} from "lucide-react";

import ExecucaoTab from "@/components/ExecucaoTab";

const FORMAS_AUTO = ["Pix ao final de cada serviço", "Pix ao final do serviço"];

const emptyItem = () => ({
  id: crypto.randomUUID(),
  descricao: "",
  quantidade: 1,
  unidade: "un",
  valor_unitario: "",
  valor_total: 0,
  observacao: "",
  ordem: 0,
  fotos: [],
});

export default function OrcamentoEditor({ overrideId, onClose, onSaved, inSheet }) {
  const params = useParams();
  const nav = useNavigate();
  const id = overrideId ?? params.id;
  const isEdit = !!id;
  const [clientes, setClientes] = useState([]);
  const [orc, setOrc] = useState({
    cliente_id: "",
    itens: [emptyItem()],
    observacao_geral: "",
    forma_pagamento: "",
    prazo_pagamento: "",
    validade_proposta: "",
    prazo_execucao: "",
    garantia: "",
    status: "rascunho",
    numero_orcamento: null,
    token_publico: null,
    id: null,
    valor_total: 0,
    cliente: null,
  });
  const [saving, setSaving] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [dividindo, setDividindo] = useState(false);
  const [tab, setTab] = useState("orcamento");

  const toggleSelectItem = (itemId) =>
    setSelectedItemIds((arr) => (arr.includes(itemId) ? arr.filter((x) => x !== itemId) : [...arr, itemId]));

  const dividirOrcamento = async () => {
    if (!orc.id || selectedItemIds.length === 0) return;
    if (selectedItemIds.length === orc.itens.length) {
      toast.error("Deixe pelo menos 1 item no orçamento original.");
      return;
    }
    if (!window.confirm(
      `Criar um novo orçamento com ${selectedItemIds.length} item(ns) para o mesmo cliente? Os demais ${orc.itens.length - selectedItemIds.length} continuam neste orçamento.`
    )) return;
    setDividindo(true);
    try {
      const { data: novo } = await api.post(`/orcamentos/${orc.id}/dividir`, { item_ids: selectedItemIds });
      toast.success(`Novo orçamento ${novo.numero_orcamento} criado. Redirecionando…`);
      setSelectedItemIds([]);
      // Recarrega o orçamento atual (agora sem os itens divididos)
      const { data: atualizado } = await api.get(`/orcamentos/${orc.id}`);
      setOrc({ ...atualizado, itens: atualizado.itens?.length ? atualizado.itens : [emptyItem()] });
      // Abre o novo em outra aba, se preferir editar depois
      setTimeout(() => window.open(`/admin/orcamentos/${novo.id}`, "_blank"), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao dividir orçamento");
    } finally {
      setDividindo(false);
    }
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/clientes");
      setClientes(data);
      if (isEdit) {
        const { data: o } = await api.get(`/orcamentos/${id}`);
        setOrc({ ...o, itens: o.itens?.length ? o.itens : [emptyItem()] });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Aceita "1,50" ou "1.50" ou número
  const toNum = (v) => {
    if (typeof v === "number") return v;
    if (!v) return 0;
    const n = parseFloat(String(v).replace(/\./g, "").replace(",", "."));
    return isNaN(n) ? 0 : n;
  };

  const total = orc.itens.reduce(
    (s, it) => s + toNum(it.quantidade) * toNum(it.valor_unitario),
    0
  );

  const updateItem = (idx, patch) => {
    const arr = [...orc.itens];
    arr[idx] = { ...arr[idx], ...patch };
    setOrc({ ...orc, itens: arr });
  };
  const nItens = orc.itens.length;
  useEffect(() => {
    setOrc((o) => {
      if (o.forma_pagamento && !FORMAS_AUTO.includes(o.forma_pagamento)) return o;
      const forma = o.itens.length > 1 ? FORMAS_AUTO[0] : FORMAS_AUTO[1];
      return o.forma_pagamento === forma ? o : { ...o, forma_pagamento: forma };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nItens, orc.id]);

  const addItem = () => setOrc({ ...orc, itens: [...orc.itens, emptyItem()] });
  const removeItem = (idx) => {
    const arr = orc.itens.filter((_, i) => i !== idx);
    setOrc({ ...orc, itens: arr.length ? arr : [emptyItem()] });
  };
  const moveItem = (idx, dir) => {
    const arr = [...orc.itens];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setOrc({ ...orc, itens: arr });
  };

  const uploadFoto = async (idx, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fotos = [...(orc.itens[idx].fotos || []), { id: data.id, url: data.url, path: data.path }];
      updateItem(idx, { fotos });
    } catch (e) {
      toast.error("Erro ao enviar foto");
    }
  };

  const removeFoto = (idx, fotoId) => {
    const fotos = orc.itens[idx].fotos.filter((f) => f.id !== fotoId);
    updateItem(idx, { fotos });
  };

  const salvar = async (novoStatus) => {
    if (!orc.cliente_id) {
      toast.error("Selecione um cliente");
      return;
    }
    if (orc.itens.some((i) => !i.descricao.trim())) {
      toast.error("Preencha a descrição de todos os itens");
      return;
    }
    const condicoes = [
      { key: "forma_pagamento", label: "Forma de pagamento" },
      { key: "validade_proposta", label: "Validade da proposta" },
      { key: "prazo_execucao", label: "Prazo de execução" },
      { key: "garantia", label: "Garantia" },
    ];
    const vazias = condicoes.filter((c) => !(orc[c.key] || "").trim());
    if (vazias.length) {
      toast.error(`Preencha as condições comerciais: ${vazias.map((c) => c.label).join(", ")}`);
      // Scrollar até a seção de condições
      setTimeout(() => {
        const el = document.querySelector('[data-testid="condicoes-section"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      return;
    }
    setSaving(true);
    const payload = {
      cliente_id: orc.cliente_id,
      itens: orc.itens.map((it) => ({
        ...it,
        quantidade: toNum(it.quantidade),
        valor_unitario: toNum(it.valor_unitario),
      })),
      observacao_geral: orc.observacao_geral,
      forma_pagamento: orc.forma_pagamento,
      prazo_pagamento: orc.prazo_pagamento,
      validade_proposta: orc.validade_proposta,
      prazo_execucao: orc.prazo_execucao,
      garantia: orc.garantia,
      status: novoStatus || orc.status,
    };
    try {
      let data;
      const targetId = id || orc.id;
      if (targetId) {
        ({ data } = await api.put(`/orcamentos/${targetId}`, payload));
      } else {
        ({ data } = await api.post("/orcamentos", payload));
      }
      toast.success(`Orçamento ${data.numero_orcamento} salvo`);
      setOrc({ ...data, itens: data.itens?.length ? data.itens : [emptyItem()] });
      if (!isEdit && !inSheet) nav(`/admin/orcamentos/${data.id}`, { replace: true });
      if (onSaved) onSaved(data);
      // Scroll até o painel de compartilhamento assim que o orçamento é salvo
      setTimeout(() => {
        const el = document.querySelector('[data-testid="compartilhar-section"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const cliente = clientes.find((c) => c.id === orc.cliente_id) || orc.cliente;

  const copiarLink = () => {
    if (!orc.token_publico) return toast.error("Salve o orçamento primeiro");
    navigator.clipboard.writeText(publicQuoteUrl(orc.token_publico));
    toast.success("Link copiado");
  };

  const copiarLinkCadastro = async () => {
    if (!cliente?.codigo_cadastro) return toast.error("Cliente sem código de cadastro");
    const partes = [cadastroClienteUrl(cliente.codigo_cadastro)];
    if (orc?.token_publico) {
      partes.push("", `Link do orçamento nº ${orc.numero_orcamento}: ${publicQuoteUrl(orc.token_publico)}`);
    }
    try {
      await navigator.clipboard.writeText(partes.join("\n"));
      toast.success(orc?.token_publico ? "Links copiados (cadastro + orçamento)" : "Link de cadastro copiado");
    } catch {
      toast.info(partes.join("\n"));
    }
  };

  const enviarLinkCadastroWhatsapp = () => {
    if (!cliente?.codigo_cadastro) return toast.error("Cliente sem código de cadastro");
    const cadastroUrl = cadastroClienteUrl(cliente.codigo_cadastro);
    const nomeC = (cliente.nome || "cliente").trim();
    const numeroOrc = orc?.numero_orcamento || "—";
    const body = [
      `Olá ${nomeC}, você está recebendo o orçamento nº ${numeroOrc} após solicitação.`,
      "",
    ];
    if (orc?.token_publico) {
      body.push("Link do orçamento:", publicQuoteUrl(orc.token_publico), "");
    }
    body.push("Link para cadastro no portal:", cadastroUrl);
    const msg = buildSistemaWhatsappMessage(body);
    openWhatsapp(cliente.whatsapp || cliente.telefone, msg);
  };

  const enviarWhatsapp = async () => {
    let o = orc;
    if (!o.id) o = await salvar("enviado");
    if (!o?.token_publico) return;
    const nomeC = (cliente?.nome || "cliente").trim();
    const msg = buildSistemaWhatsappMessage([
      `Olá ${nomeC}, você está recebendo o orçamento nº ${o.numero_orcamento} após solicitação.`,
      "",
      "Link do orçamento:",
      publicQuoteUrl(o.token_publico),
    ]);
    // Abre o WhatsApp IMEDIATAMENTE dentro do gesto do usuário (senão o navegador bloqueia popup)
    openWhatsapp(cliente?.whatsapp || cliente?.telefone, msg);
    toast.success("WhatsApp aberto com o link do orçamento.");
    // Trabalho de fundo depois do open
    try {
      if (o.status === "rascunho") await api.patch(`/orcamentos/${o.id}/status`, { status: "enviado" });
      await api.post(`/orcamentos/${o.id}/registrar-envio`, {
        tipo: "whatsapp",
        contato: (cliente?.whatsapp || cliente?.telefone || "").replace(/\D/g, "") || null,
      });
      const { data } = await api.get(`/orcamentos/${o.id}`);
      setOrc({ ...data, itens: data.itens?.length ? data.itens : orc.itens });
    } catch { /* silencioso */ }
  };

  const aprovarOrc = async () => {
    if (!orc.id) return toast.error("Salve o orçamento primeiro");
    if (!window.confirm(`Aprovar orçamento ${orc.numero_orcamento}?`)) return;
    try {
      await api.post(`/orcamentos/${orc.id}/aprovar`);
      toast.success("Orçamento aprovado");
      const { data } = await api.get(`/orcamentos/${orc.id}`);
      setOrc({ ...data, itens: data.itens?.length ? data.itens : orc.itens });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const reprovarOrc = async () => {
    if (!orc.id) return toast.error("Salve o orçamento primeiro");
    const motivo = window.prompt("Motivo da reprovação (opcional):");
    if (motivo === null) return;
    try {
      await api.post(`/orcamentos/${orc.id}/recusar`, { motivo: motivo || null });
      toast.success("Orçamento reprovado");
      const { data } = await api.get(`/orcamentos/${orc.id}`);
      setOrc({ ...data, itens: data.itens?.length ? data.itens : orc.itens });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const reabrirOrc = async () => {
    if (!window.confirm("Reabrir orçamento?")) return;
    try {
      await api.post(`/orcamentos/${orc.id}/reabrir`);
      toast.success("Orçamento reaberto");
      const { data } = await api.get(`/orcamentos/${orc.id}`);
      setOrc({ ...data, itens: data.itens?.length ? data.itens : orc.itens });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const finalizarOrc = async () => {
    if (!window.confirm("Marcar este orçamento como finalizado? O serviço foi concluído?")) return;
    try {
      await api.post(`/orcamentos/${orc.id}/finalizar`);
      toast.success("Orçamento finalizado");
      const { data } = await api.get(`/orcamentos/${orc.id}`);
      setOrc({ ...data, itens: data.itens?.length ? data.itens : orc.itens });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const enviarLembrete = () => {
    if (!cliente) return toast.error("Selecione um cliente");
    const numero = (cliente.whatsapp || cliente.telefone || "").replace(/\D/g, "");
    const dias = orc.dias_para_expirar;
    const linhas = [
      `Olá, ${cliente.nome}.`,
      "",
      `Passando para lembrar do orçamento nº ${orc.numero_orcamento} enviado pela BBH.`,
      dias === 0
        ? "Ele expira hoje."
        : dias === 1
          ? "Ele expira amanhã."
          : dias && dias <= 5
            ? `Ele expira em ${dias} dias.`
            : "Aguardamos sua avaliação.",
      "",
      "Link do orçamento:",
      publicQuoteUrl(orc.token_publico),
    ];
    const msg = encodeURIComponent(linhas.join("\n"));
    const url = numero ? `https://wa.me/${numero}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="orcamento-editor">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => (inSheet && onClose ? onClose() : nav(-1))} className="p-2 rounded hover:bg-zinc-100" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
              {isEdit ? "Edição" : "Novo"}
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              {orc.numero_orcamento ? `Orçamento Nº ${orc.numero_orcamento}` : "Novo Orçamento"}
            </h1>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {orc.id && orc.status !== "aprovado" && orc.status !== "finalizado" && (
            <button
              onClick={aprovarOrc}
              data-testid="editor-aprovar-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            >
              <CheckCircle2 className="w-4 h-4" /> Aprovar
            </button>
          )}
          {orc.id && orc.status !== "recusado" && orc.status !== "aprovado" && orc.status !== "finalizado" && (
            <button
              onClick={reprovarOrc}
              data-testid="editor-reprovar-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
            >
              <XCircle className="w-4 h-4" /> Reprovar
            </button>
          )}
          {orc.id && orc.status === "aprovado" && (
            <button
              onClick={finalizarOrc}
              data-testid="editor-finalizar-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
            >
              <CheckCircle2 className="w-4 h-4" /> Finalizar
            </button>
          )}
          {orc.id && orc.status === "recusado" && (
            <button
              onClick={reabrirOrc}
              data-testid="editor-reabrir-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold"
            >
              <RotateCcw className="w-4 h-4" /> Reabrir
            </button>
          )}
          <button
            onClick={() => salvar()}
            disabled={saving}
            data-testid="salvar-orcamento-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-zinc-900 text-white text-sm font-semibold disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      {orc.id && (
        <div className="flex gap-1 border-b border-zinc-200" data-testid="editor-tabs">
          {[
            { key: "orcamento", label: "Orçamento" },
            { key: "execucao", label: "Execução" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${
                tab === t.key
                  ? "border-[#14243D] text-[#14243D]"
                  : "border-transparent text-zinc-500 hover:text-zinc-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {tab === "execucao" && orc.id ? (
        <ExecucaoTab
          orc={orc}
          setOrc={setOrc}
          cliente={cliente}
          onReload={async () => {
            const { data } = await api.get(`/orcamentos/${orc.id}`);
            setOrc({ ...data, itens: data.itens?.length ? data.itens : [emptyItem()] });
          }}
        />
      ) : (
      <>

      {/* Vencimento / Lembrete */}
      {orc.id && (orc.dias_para_expirar !== null && orc.dias_para_expirar !== undefined) && (orc.status === "enviado" || orc.status === "visualizado" || orc.status === "rascunho") && (
        <div
          className={`bbh-card p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-l-4 ${orc.dias_para_expirar <= 2 ? "border-l-amber-500 bg-amber-50" : "border-l-zinc-300"}`}
          data-testid="vencimento-alert"
        >
          <div className="flex items-center gap-3">
            {orc.dias_para_expirar <= 2 ? <AlertTriangle className="w-5 h-5 text-amber-600" /> : <Bell className="w-5 h-5 text-zinc-500" />}
            <div className="text-sm">
              {orc.dias_para_expirar === 0
                ? <span className="font-semibold text-amber-700">Expira hoje.</span>
                : orc.dias_para_expirar <= 2
                  ? <span className="font-semibold text-amber-700">Expira em {orc.dias_para_expirar} dia(s).</span>
                  : <span className="text-zinc-700">Expira em {orc.dias_para_expirar} dia(s).</span>}
              <span className="text-zinc-500"> Envie um lembrete pelo WhatsApp.</span>
            </div>
          </div>
          <button
            onClick={enviarLembrete}
            data-testid="enviar-lembrete-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
          >
            <MessageCircle className="w-4 h-4" /> Enviar lembrete
          </button>
        </div>
      )}
      {orc.status === "expirado" && (
        <div className="bbh-card p-4 border-l-4 border-l-zinc-400 bg-zinc-50 flex items-center gap-3" data-testid="expirado-alert">
          <AlertTriangle className="w-5 h-5 text-zinc-500" />
          <div className="text-sm text-zinc-700">Orçamento expirado. Reabra para retomar as ações.</div>
        </div>
      )}

      {/* Cliente */}
      <section className="bbh-card p-5">        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">
          Cliente
        </div>
        <select
          value={orc.cliente_id}
          onChange={(e) => setOrc({ ...orc, cliente_id: e.target.value })}
          data-testid="cliente-select"
          className="w-full px-3 py-2.5 rounded-md border border-zinc-200 bbh-input text-sm bg-white"
        >
          <option value="">Selecione um cliente...</option>
          {clientes.map((c) => (
            <option key={c.id} value={c.id}>{`${c.codigo_cliente} - ${c.nome}`}</option>
          ))}
        </select>
        {cliente && (
          <div className="mt-3 text-sm text-zinc-600 grid grid-cols-1 sm:grid-cols-2 gap-1">
            <div><span className="text-zinc-400">Endereço:</span> {cliente.endereco || "-"}</div>
            <div><span className="text-zinc-400">WhatsApp:</span> {cliente.whatsapp || "-"}</div>
            <div><span className="text-zinc-400">E-mail:</span> {cliente.email || "-"}</div>
          </div>
        )}
      </section>

      {/* Observação do cliente */}
      {orc.observacao_cliente && (
        <section className="bbh-card p-5 border-l-4 border-l-emerald-500" data-testid="obs-cliente-section">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-emerald-700">
              Mensagem do cliente
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold uppercase tracking-widest">
              Nova
            </span>
          </div>
          <p className="text-sm whitespace-pre-wrap text-zinc-800" data-testid="obs-cliente-texto">
            {orc.observacao_cliente}
          </p>
        </section>
      )}

      {/* Pedido de reabertura */}
      {orc.pedido_reabertura && (
        <section className="bbh-card p-5 border-l-4 border-l-amber-500" data-testid="pedido-reabertura-section">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-amber-700">
              Pedido de reabertura do cliente
            </div>
          </div>
          <div className="text-xs text-zinc-500">
            Solicitado em {orc.pedido_reabertura.solicitada_em && new Date(orc.pedido_reabertura.solicitada_em).toLocaleString("pt-BR")}
          </div>
          {orc.pedido_reabertura.motivo && (
            <p className="text-sm whitespace-pre-wrap text-zinc-800 mt-2">
              {orc.pedido_reabertura.motivo}
            </p>
          )}
        </section>
      )}

      {/* Itens */}
      <section className="bbh-card p-5" data-testid="itens-section">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
            Serviços
          </div>
          <div className="flex items-center gap-2">
            {orc.id && selectedItemIds.length > 0 && (
              <button
                onClick={dividirOrcamento}
                disabled={dividindo}
                data-testid="dividir-orcamento-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-semibold"
              >
                <Scissors className="w-3.5 h-3.5" />
                {dividindo ? "Dividindo…" : `Dividir (${selectedItemIds.length})`}
              </button>
            )}
            <button
              onClick={addItem}
              data-testid="add-item-btn"
              className="inline-flex items-center gap-1 text-sm text-[#14243D] font-semibold"
            >
              <Plus className="w-4 h-4" /> Adicionar item
            </button>
          </div>
        </div>
        {orc.id && (
          <p className="text-xs text-zinc-500 mb-3">
            💡 Marque os itens abaixo para dividir em um novo orçamento (o cliente mantém os demais).
          </p>
        )}
        <div className="space-y-4">
          {orc.itens.map((it, idx) => (
            <ItemRow
              key={it.id}
              item={it}
              idx={idx}
              total={orc.itens.length}
              selectable={!!orc.id}
              selected={selectedItemIds.includes(it.id)}
              onToggleSelect={() => toggleSelectItem(it.id)}
              onChange={(patch) => updateItem(idx, patch)}
              onRemove={() => removeItem(idx)}
              onMove={(d) => moveItem(idx, d)}
              onUpload={(f) => uploadFoto(idx, f)}
              onRemoveFoto={(fid) => removeFoto(idx, fid)}
            />
          ))}
        </div>
        <button
          onClick={addItem}
          data-testid="add-item-bottom-btn"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3 rounded-md border-2 border-dashed border-zinc-300 hover:border-[#14243D] hover:text-[#14243D] text-zinc-600 text-sm font-semibold transition-colors"
        >
          <Plus className="w-4 h-4" /> Adicionar novo item
        </button>
        <div className="mt-6 pt-4 border-t border-zinc-200 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
            Valor Total
          </span>
          <span className="font-heading text-3xl font-bold bbh-accent" data-testid="valor-total">
            {money(total)}
          </span>
        </div>
      </section>

      {/* Condições */}
      <section className="bbh-card p-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="condicoes-section">
        <div className="col-span-full text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
          Condições Comerciais <span className="text-rose-500">*</span>
        </div>
        <Field label="Forma de pagamento" testid="forma-pagamento" value={orc.forma_pagamento}
          onChange={(v) => setOrc({ ...orc, forma_pagamento: v })} />
        <Field label="Validade da proposta" testid="validade-proposta" value={orc.validade_proposta}
          onChange={(v) => setOrc({ ...orc, validade_proposta: v })} />
        <Field label="Prazo de execução" testid="prazo-execucao" value={orc.prazo_execucao}
          onChange={(v) => setOrc({ ...orc, prazo_execucao: v })} />
        <Field label="Garantia" testid="garantia" value={orc.garantia}
          onChange={(v) => setOrc({ ...orc, garantia: v })} />
        <div className="col-span-full flex justify-end">
          <button
            onClick={() => salvar()}
            disabled={saving}
            data-testid="salvar-condicoes-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-zinc-900 text-white text-sm font-semibold disabled:opacity-50 w-full sm:w-auto justify-center"
          >
            <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar orçamento"}
          </button>
        </div>
      </section>

      {/* Observação */}
      <section className="bbh-card p-5">
        <label className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
          Observações Gerais
        </label>
        <textarea
          data-testid="observacao-geral"
          value={orc.observacao_geral}
          onChange={(e) => setOrc({ ...orc, observacao_geral: e.target.value })}
          rows={4}
          className="mt-2 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm"
        />
      </section>

      {/* Ações pós salvar */}
      {orc.token_publico && (
        <section className="bbh-card p-5" data-testid="compartilhar-section">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">
            Compartilhar
          </div>
          <div className="text-xs text-zinc-500 break-all mb-3">{publicQuoteUrl(orc.token_publico)}</div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={copiarLink}
              data-testid="copiar-link-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-zinc-200 text-sm font-medium hover:bg-zinc-50"
            >
              <Copy className="w-4 h-4" /> Copiar link
            </button>
            <a
              href={publicQuoteUrl(orc.token_publico)}
              target="_blank"
              rel="noreferrer"
              data-testid="visualizar-cliente-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-zinc-200 text-sm font-medium hover:bg-zinc-50"
            >
              <ExternalLink className="w-4 h-4" /> Ver como cliente
            </a>
            <a
              href={adminPdfUrl(orc.id)}
              target="_blank"
              rel="noreferrer"
              data-testid="visualizar-pdf-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-zinc-200 text-sm font-medium hover:bg-zinc-50"
            >
              <Eye className="w-4 h-4" /> Visualizar PDF
            </a>
            <a
              href={adminPdfUrl(orc.id, true)}
              data-testid="baixar-pdf-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-zinc-200 text-sm font-medium hover:bg-zinc-50"
            >
              <FileDown className="w-4 h-4" /> Baixar PDF
            </a>
            <button
              onClick={enviarWhatsapp}
              data-testid="enviar-whatsapp-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            >
              <MessageCircle className="w-4 h-4" /> Enviar pelo WhatsApp
            </button>
          </div>

          {/* Cadastro do cliente no portal (só quando ainda não cadastrado) */}
          {cliente?.codigo_cadastro && !cliente?.ja_cadastrado && (
            <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="cadastro-cliente-block">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">
                Cadastro do cliente no portal
              </div>
              <div className="text-xs text-zinc-500 break-all mb-3">
                {cadastroClienteUrl(cliente.codigo_cadastro)}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={copiarLinkCadastro}
                  data-testid="copiar-link-cadastro-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-zinc-200 text-sm font-medium hover:bg-zinc-50"
                >
                  <Copy className="w-4 h-4" /> Copiar link de cadastro
                </button>
                <button
                  type="button"
                  onClick={enviarLinkCadastroWhatsapp}
                  data-testid="wpp-link-cadastro-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                >
                  <MessageCircle className="w-4 h-4" /> Enviar cadastro por WhatsApp
                </button>
              </div>
              <p className="text-[11px] text-zinc-400 mt-2">
                Ao se cadastrar por este link, o cliente é automaticamente vinculado e passa a ver todos os orçamentos dele no portal.
              </p>
            </div>
          )}
          {cliente?.ja_cadastrado && (
            <div className="mt-4 pt-4 border-t border-zinc-100 text-xs text-zinc-500 flex items-center gap-2" data-testid="cliente-ja-cadastrado-info">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" /> Cliente já tem conta no portal
              </span>
              — receberá o e-mail com o link do orçamento automaticamente ao enviar.
            </div>
          )}

          {/* Histórico de envios */}
          {Array.isArray(orc.historico_envios) && orc.historico_envios.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="historico-envios">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">
                Histórico de envios
              </div>
              <ul className="space-y-1.5">
                {orc.historico_envios.slice().reverse().map((h, i) => (
                  <li
                    key={i}
                    data-testid={`envio-${orc.historico_envios.length - 1 - i}`}
                    className="flex items-start gap-2 text-xs text-zinc-700"
                  >
                    {h.tipo === "email" ? (
                      <span className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">
                        <Send className="w-3 h-3" /> E-mail
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                        <MessageCircle className="w-3 h-3" /> WhatsApp
                      </span>
                    )}
                    <span className="text-zinc-600">
                      Enviado {h.tipo === "email" ? "por e-mail" : "pelo WhatsApp"}
                      {h.contato ? ` para ${h.contato}` : ""} em <b>{formatDateTime(h.quando)}</b>
                      {h.sucesso === false && <span className="text-rose-600 ml-1">(falhou)</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
      </>
      )}
    </div>
  );
}

function ItemRow({ item, idx, total, selectable, selected, onToggleSelect, onChange, onRemove, onMove, onUpload, onRemoveFoto }) {
  const inputRef = useRef();
  const _n = (v) => {
    if (typeof v === "number") return v;
    if (!v) return 0;
    const n = parseFloat(String(v).replace(/\./g, "").replace(",", "."));
    return isNaN(n) ? 0 : n;
  };
  const subtotal = _n(item.quantidade) * _n(item.valor_unitario);
  // Existing items (with description) render collapsed; new ones render expanded.
  const [expanded, setExpanded] = useState(!item.descricao);
  const canCollapse = !!(item.descricao && item.descricao.trim());

  if (!expanded) {
    return (
      <div
        className={`border rounded-md p-3 bg-white flex items-center gap-3 hover:border-[#14243D] transition-colors ${
          selected ? "border-indigo-500 ring-2 ring-indigo-100" : "border-zinc-200"
        }`}
        data-testid={`item-collapsed-${idx}`}
      >
        {selectable && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`item-select-${idx}`}
            className="w-4 h-4 accent-indigo-600 cursor-pointer shrink-0"
            aria-label="Selecionar item para dividir"
          />
        )}
        <div className="flex flex-col gap-0.5 shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onMove(-1); }}
            disabled={idx === 0}
            className="text-zinc-400 hover:text-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`item-up-${idx}`}
            aria-label="Subir"
          >
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onMove(1); }}
            disabled={idx === total - 1}
            className="text-zinc-400 hover:text-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`item-down-${idx}`}
            aria-label="Descer"
          >
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
        </div>
        <div
          className="text-xs font-mono text-zinc-400 w-6 text-center shrink-0 cursor-pointer"
          onClick={() => setExpanded(true)}
        >
          {idx + 1}
        </div>
        <div className="flex-1 flex items-center gap-3 cursor-pointer min-w-0" onClick={() => setExpanded(true)}>
          {item.fotos?.[0] ? (
            <img src={fileUrl(item.fotos[0].path)} alt="" className="w-12 h-12 rounded-md object-cover border border-zinc-200 shrink-0" />
          ) : (
            <div className="w-12 h-12 rounded-md bg-zinc-50 border border-zinc-200 flex items-center justify-center shrink-0">
              <ImageIcon className="w-4 h-4 text-zinc-300" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">{item.descricao}</div>
            <div className="text-xs text-zinc-500">
              {Number(item.quantidade || 0).toLocaleString("pt-BR")} × {money(item.valor_unitario)}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-heading font-semibold text-sm">{money(subtotal)}</div>
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
            className="p-2 rounded hover:bg-zinc-100 text-zinc-600"
            aria-label="Editar item"
            data-testid={`item-expand-${idx}`}
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="p-2 rounded hover:bg-rose-50 text-rose-600"
            aria-label="Excluir item"
            data-testid={`remove-item-${idx}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`border rounded-md p-4 bg-white ${selected ? "border-indigo-500 ring-2 ring-indigo-100" : "border-zinc-200"}`} data-testid={`item-${idx}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="flex flex-col items-center gap-0.5 mt-1">
          {selectable && (
            <input
              type="checkbox"
              checked={!!selected}
              onChange={onToggleSelect}
              data-testid={`item-select-expanded-${idx}`}
              className="w-4 h-4 accent-indigo-600 cursor-pointer mb-1"
              aria-label="Selecionar item para dividir"
            />
          )}
          <button onClick={() => onMove(-1)} disabled={idx === 0} className="text-zinc-400 hover:text-zinc-700 disabled:opacity-30" aria-label="Subir">
            <ArrowUp className="w-4 h-4" />
          </button>
          <span className="text-xs font-mono text-zinc-400">{idx + 1}</span>
          <button onClick={() => onMove(1)} disabled={idx === total - 1} className="text-zinc-400 hover:text-zinc-700 disabled:opacity-30" aria-label="Descer">
            <ArrowDown className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1">
          <AutoTextarea
            value={item.descricao}
            onChange={(v) => onChange({ descricao: v })}
            placeholder="Descrição do serviço *"
            data-testid={`item-descricao-${idx}`}
            className="w-full px-3 py-2.5 rounded-md border border-zinc-200 bbh-input text-base bg-white font-medium leading-snug"
            spellCheck={true}
            autoCorrect="on"
            autoCapitalize="sentences"
            lang="pt-BR"
            minRows={1}
          />
        </div>
        <button onClick={onRemove} className="p-2 rounded hover:bg-rose-50 text-rose-600" data-testid={`remove-item-${idx}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <NumField label="Qnd" testid={`item-quantidade-${idx}`} value={item.quantidade}
          onChange={(v) => onChange({ quantidade: v })} />
        <NumField label="Valor Uni" placeholder="0,00" testid={`item-valor-${idx}`} value={item.valor_unitario}
          onChange={(v) => onChange({ valor_unitario: v })} />
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
            Total
          </label>
          <div className="mt-1 px-3 py-2.5 rounded-md bg-zinc-50 border border-zinc-200 text-base font-heading font-semibold">
            {money(subtotal)}
          </div>
        </div>
      </div>

      <AutoTextarea
        value={item.observacao}
        onChange={(v) => onChange({ observacao: v })}
        placeholder="Observação do item (opcional)"
        data-testid={`item-observacao-${idx}`}
        className="w-full px-3 py-2.5 rounded-md border border-zinc-200 bbh-input text-base mb-3"
        spellCheck={true}
        autoCorrect="on"
        autoCapitalize="sentences"
        lang="pt-BR"
        minRows={2}
      />

      <div className="flex items-center gap-3 flex-wrap mb-3">
        {(item.fotos || []).map((f) => (
          <div key={f.id} className="relative w-20 h-20 rounded-md overflow-hidden border border-zinc-200">
            <img src={fileUrl(f.path)} alt="" className="bbh-thumb" />
            <button
              onClick={() => onRemoveFoto(f.id)}
              className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"
              data-testid={`remove-foto-${idx}-${f.id}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          onClick={() => inputRef.current?.click()}
          data-testid={`upload-foto-${idx}`}
          className="w-20 h-20 rounded-md border-2 border-dashed border-zinc-300 flex flex-col items-center justify-center text-zinc-500 hover:border-[#14243D] hover:text-[#14243D] text-xs"
        >
          <Upload className="w-4 h-4 mb-1" /> Foto
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
        />
      </div>

      {/* Ações do item */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
        <button
          onClick={() => {
            setExpanded(false);
            requestAnimationFrame(() => {
              document.querySelector('[data-testid="add-item-bottom-btn"]')?.scrollIntoView({ block: "center" });
            });
          }}
          disabled={!canCollapse}
          data-testid={`item-collapse-${idx}`}
          title={canCollapse ? "Recolher item" : "Preencha a descrição"}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#14243D] hover:bg-[#0B1628] disabled:opacity-40 text-white text-sm font-semibold"
        >
          <Check className="w-4 h-4" /> Gravar item
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm bg-white"
      />
    </div>
  );
}
const toBRDisplay = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (!v) return "";
  return v.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
};

// Formata enquanto digita: "1500,5" -> "1.500,5" (ponto digitado vira vírgula decimal)
const formatBRTyping = (raw, prev) => {
  let s = raw;
  if (s.endsWith(".") && s.length > (prev || "").length) s = s.slice(0, -1) + ",";
  s = s.replace(/[^\d,]/g, "");
  const [intRaw, ...rest] = s.split(",");
  const hasComma = s.includes(",");
  const dec = rest.join("").slice(0, 3);
  let int = intRaw.replace(/^0+(?=\d)/, "");
  if (!int && hasComma) int = "0";
  int = int.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return hasComma ? `${int},${dec}` : int;
};

function NumField({ label, value, onChange, testid, placeholder }) {
  const display = toBRDisplay(value);
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">{label}</label>
      <input
        type="text"
        inputMode="decimal"
        value={display}
        placeholder={placeholder}
        onChange={(e) => onChange(formatBRTyping(e.target.value, display))}
        data-testid={testid}
        className="mt-1 w-full px-3 py-2.5 rounded-md border border-zinc-200 bbh-input text-base bg-white"
      />
    </div>
  );
}

function AutoTextarea({ value, onChange, minRows = 1, className = "", ...rest }) {
  const ref = useRef(null);
  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };
  useEffect(() => { resize(); }, [value]);
  return (
    <textarea
      ref={ref}
      value={value || ""}
      onChange={(e) => { onChange(e.target.value); resize(); }}
      rows={minRows}
      className={className + " resize-none overflow-hidden"}
      {...rest}
    />
  );
}
